import React from 'react';
import { Container } from 'react-bootstrap';
import { useLocation, Navigate } from 'react-router-dom';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { BreadcrumbComponent } from '../../Components/BreadCrumb';
import BuildingCheckTemplatesEditor from '../BuildingChecks/BuildingCheckTemplatesEditor';
import ManageUsers from './ManageUsers';
import CategoriesManagementPage from './CategoriesManagementPage';
import ComponentTypesManagementPage from './ComponentTypesManagementPage';
import MaterialsManagementPage from './MaterialsManagementPage';
import NotificationRulesPage from './NotificationRulesPage';
import WorkOrderReminderSettings from './WorkOrderReminderSettings';
import LoginPageSettings from './LoginPageSettings';
import PublicReportSettings from './PublicReportSettings';
import ParkingQueueSettings from './ParkingQueueSettings';
import FeaturesSettings from './FeaturesSettings';
import AutoAssignmentSettings from './AutoAssignmentSettings';
import FeatureRoute from '../../Components/FeatureRoute';
import './SettingsPage.css';

type SettingsTab = 'general' | 'features' | 'categories' | 'component-types' | 'materials' | 'building-checks' | 'manage-users' | 'notifications' | 'auto-assignment' | 'public-report' | 'parking-queue';

const VALID_TABS: SettingsTab[] = ['general', 'features', 'categories', 'component-types', 'materials', 'building-checks', 'manage-users', 'notifications', 'auto-assignment', 'public-report', 'parking-queue'];

// Tab titles for the page header
const TAB_TITLES: Record<SettingsTab, string> = {
    'general': 'Allmänt',
    'features': 'Funktioner',
    'categories': 'Kategorier & taggar',
    'component-types': 'Komponenttyper & underhållsmallar',
    'materials': 'Materialbibliotek',
    'building-checks': 'Kontrollmallar',
    'manage-users': 'Hantera användare',
    'notifications': 'Aviseringar',
    'auto-assignment': 'Automatisk tilldelning',
    'public-report': 'Publik felanmälan',
    'parking-queue': 'Parkeringskö'
};

const SettingsPage = () => {
    const location = useLocation();

    // Extract tab from URL path: /settings/manage-users -> 'manage-users'
    // Handle nested paths like /settings/categories/location -> 'categories'
    const pathParts = location.pathname.split('/').filter(Boolean);
    const settingsIndex = pathParts.indexOf('settings');
    const tabFromUrl = settingsIndex >= 0 && pathParts.length > settingsIndex + 1
        ? pathParts[settingsIndex + 1]
        : 'general';

    // Validate and determine active tab
    const isValidTab = VALID_TABS.includes(tabFromUrl as SettingsTab);
    const activeTab: SettingsTab = isValidTab ? (tabFromUrl as SettingsTab) : 'general';

    // Set document title
    useDocumentTitle(`${TAB_TITLES[activeTab]} - Inställningar`);

    // Redirect to /settings/general if at /settings with no subpath
    if (location.pathname === '/settings' || location.pathname === '/settings/') {
        return <Navigate to="/settings/general" replace />;
    }

    // Redirect old external-users URL to manage-users (external users is now a tab there)
    if (tabFromUrl === 'external-users') {
        return <Navigate to="/settings/manage-users" replace />;
    }

    return (
        <div className="settings-page">
            <BreadcrumbComponent
                rootItem={null}
                items={[
                    { text: 'Inställningar' },
                    { text: TAB_TITLES[activeTab] }
                ]}
            />
            <div className="settings-header">
                <h1 className="settings-title page-title">{TAB_TITLES[activeTab]}</h1>
            </div>

            <div className="settings-content">
                <Container fluid className="gx-0">
                    {activeTab === 'general' && (
                        <div style={{ maxWidth: 800 }}>
                            <LoginPageSettings />
                        </div>
                    )}
                    {activeTab === 'features' && (
                        <FeaturesSettings />
                    )}
                    {activeTab === 'categories' && (
                        <CategoriesManagementPage />
                    )}
                    {activeTab === 'component-types' && (
                        <ComponentTypesManagementPage />
                    )}
                    {activeTab === 'materials' && (
                        <MaterialsManagementPage />
                    )}
                    {activeTab === 'building-checks' && (
                        <BuildingCheckTemplatesEditor />
                    )}
                    {activeTab === 'manage-users' && (
                        <ManageUsers />
                    )}
                    {activeTab === 'notifications' && (
                        <>
                            <NotificationRulesPage />
                            <WorkOrderReminderSettings />
                        </>
                    )}
                    {activeTab === 'auto-assignment' && (
                        <AutoAssignmentSettings />
                    )}
                    {activeTab === 'public-report' && (
                        <PublicReportSettings />
                    )}
                    {activeTab === 'parking-queue' && (
                        <FeatureRoute feature="parking"><ParkingQueueSettings /></FeatureRoute>
                    )}
                </Container>
            </div>
        </div>
    );
};

export default SettingsPage;
